import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import {
  CONNECTION_WORKSPACE_TABS,
  type ConnectionWorkspaceTab,
  DEFAULT_CONNECTION_WORKSPACE_TAB,
} from '~/features/deliveries/constants/connection-tabs';
import {
  parseWorkspaceReturn,
  type WorkspaceReturnContext,
} from '~/features/deliveries/utils/workspace-return';

const ConnectionWorkspacePage = lazy(
  () => import('~/features/deliveries/pages/connection-workspace-page'),
);

/** Search params of the connection workspace. */
export interface ConnectionWorkspaceSearch extends WorkspaceReturnContext {
  tab: ConnectionWorkspaceTab;
  /** Pre-fills the Products tab lookup (deep link from a run's product leaf). */
  sku?: string;
  /** Opens one staged feed's review inside the Feeds tab. */
  uploadId?: string;
}

/**
 * Meta delivery workspace for one connection — inherits the parent route's
 * `requireTenantAdmin()` guard. `tab` selects the panel; `sku` / `uploadId` deep-link
 * into the Products / Feeds tabs so support links and post-generate hand-offs survive reload.
 */
export const Route = createFileRoute(
  '/_authenticated/_tenant/deliveries/connections/$connectionId',
)({
  validateSearch: (search: Record<string, unknown>): ConnectionWorkspaceSearch => {
    const tab = CONNECTION_WORKSPACE_TABS.includes(search.tab as ConnectionWorkspaceTab)
      ? (search.tab as ConnectionWorkspaceTab)
      : DEFAULT_CONNECTION_WORKSPACE_TAB;
    const out: ConnectionWorkspaceSearch = { tab };
    if (typeof search.sku === 'string' && search.sku !== '') out.sku = search.sku;
    if (typeof search.uploadId === 'string' && search.uploadId !== '') {
      out.uploadId = search.uploadId;
    }
    // Back-link target: a delivery run (`fromRun`) or a product detail page (`source`).
    return { ...out, ...parseWorkspaceReturn(search) };
  },
  component: RouteComponent,
});

/** Bridges the route's path/search params into the workspace page props. */
function RouteComponent() {
  const { connectionId } = Route.useParams();
  const { tab, sku, uploadId, ...returnTo } = Route.useSearch();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <ConnectionWorkspacePage
        connectionId={connectionId}
        tab={tab}
        sku={sku}
        uploadId={uploadId}
        returnTo={returnTo}
      />
    </Suspense>
  );
}
